import { CancelSubscriptionRequestSource } from '@common/credits/ProfessionalEdition/constants';
import { ProfessionalMeteringInfo } from '@common/credits/ProfessionalEdition/types';
import { makeGetCall, makePostCall } from 'frontend/core/src/data/util';
import { APIUrls, hostUrl } from 'frontend/core/src/utils/getServerUrl';

/**
 * Upgrade a user to Pro
 * @param user - The user to upgrade
 * @returns The upgraded user
 */
export const upgradeToPro = async (user: { email: string; name: string; id: string; teamId: string }) => {
  return makePostCall(APIUrls.UPGRADE_TO_PRO.url, {
    plan: 'Pro',
    user,
    allowRequestOrigin: true
  });
};

/**
 * Get the customer portal for a user
 * @param userId - The ID of the user to get the customer stripe portal for
 * @returns The customer stripe portal for the user
 */
export const getCustomerPortal = async (userId: string, teamId: string) => {
  return makePostCall(APIUrls.GET_CUSTOMER_STRIPE_PORTAL.url, {
    userId,
    teamId
  });
};

/**
 * Get the saved payment methods for a user
 * @param userId - The ID of the user to get the payment methods for
 * @returns The payment methods for the user
 */
export const getPaymentMethods = async (userId: string, teamId: string) => {
  return makeGetCall(`${APIUrls.GET_PAYMENT_METHODS.url}?userId=${userId}&teamId=${teamId}`);
};

/**
 * Update the usage limit for a team
 * @param teamId - The ID of the team to update
 * @param usageLimit - The usage limit to update
 * @returns The updated usage limit
 */
export const updateUsageLimit = async (teamId: string, usageLimit: ProfessionalMeteringInfo['usageLimit']) => {
  return makePostCall(`${hostUrl}/api/professional/usageLimit/${teamId}`, {
    ...usageLimit
  });
};

/**
 * Cancel the subscription for a team
 * @param teamId - The ID of the team to cancel the subscription for
 * @param userEmail - The email of the user to cancel the subscription for
 * @param workspaceAlias - The alias of the workspace to cancel the subscription for
 * @param requestSource - Whether the request is a full cancellation or a switch to the Starter plan
 * @returns The status of the subscription cancellation
 */
export const cancelSubscription = async (
  teamId: string,
  userEmail: string,
  workspaceAlias: string,
  requestSource: CancelSubscriptionRequestSource
) => {
  return makePostCall(`${hostUrl}/api/vertical/metering/${teamId}/cancel`, {
    userEmail,
    workspaceAlias,
    requestSource
  });
};
